import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { CheckCircle2, Circle, Clock, Play, ChevronRight } from "lucide-react";

const MEETING_STAGES = [
  { key: "opening", label: "Opening" },
  { key: "attendance", label: "Attendance & Quorum" },
  { key: "agenda_approval", label: "Agenda Approval" },
  { key: "previous_followup", label: "Previous Minutes Follow-up" },
  { key: "agenda_discussion", label: "Agenda Discussion" },
  { key: "resolutions", label: "Resolutions Summary" },
  { key: "closing", label: "Closing" },
];

interface MeetingFlowPanelProps {
  meetingId: string;
  isChairOrSecretary: boolean;
}

export default function MeetingFlowPanel({ meetingId, isChairOrSecretary }: MeetingFlowPanelProps) {
  const { user } = useAuth();
  const [events, setEvents] = useState<any[]>([]);
  const [currentStage, setCurrentStage] = useState<string | null>(null);
  const [completedStages, setCompletedStages] = useState<Set<string>>(new Set());
  const [stageTimers, setStageTimers] = useState<Record<string, number>>({});
  const [activeTimer, setActiveTimer] = useState<string | null>(null);
  const [agendaItems, setAgendaItems] = useState<any[]>([]);
  const [currentAgendaIndex, setCurrentAgendaIndex] = useState(0);

  useEffect(() => {
    loadEvents();
    loadAgendaItems();
  }, [meetingId]);

  // Timer tick
  useEffect(() => {
    if (!activeTimer) return;
    const interval = setInterval(() => {
      setStageTimers(prev => ({
        ...prev,
        [activeTimer]: (prev[activeTimer] || 0) + 1,
      }));
    }, 1000);
    return () => clearInterval(interval);
  }, [activeTimer]);

  const loadEvents = async () => {
    const { data } = await supabase
      .from("meeting_events")
      .select("*")
      .eq("meeting_id", meetingId)
      .order("created_at", { ascending: true });

    const evts = data || [];
    setEvents(evts);

    // Rebuild state from events
    const completed = new Set<string>();
    let current: string | null = null;
    evts.forEach((e: any) => {
      if (e.event_type.endsWith("_started")) {
        current = e.event_type.replace("_started", "");
      }
      if (e.event_type.endsWith("_completed")) {
        completed.add(e.event_type.replace("_completed", ""));
      }
    });
    setCompletedStages(completed);
    if (current && !completed.has(current)) {
      setCurrentStage(current);
      setActiveTimer(current);
    }
  };

  const loadAgendaItems = async () => {
    const { data } = await supabase
      .from("meeting_agenda_items")
      .select("*")
      .eq("meeting_id", meetingId)
      .order("sort_order", { ascending: true });
    setAgendaItems(data || []);
  };

  const logEvent = async (eventType: string, payload: any = {}) => {
    if (!user) return;
    await supabase.from("meeting_events").insert({
      meeting_id: meetingId,
      event_type: eventType,
      actor_user_id: user.id,
      payload_json: payload,
    } as any);
  };

  const startStage = useCallback(async (stageKey: string) => {
    // Complete current stage if any
    if (currentStage && !completedStages.has(currentStage)) {
      await logEvent(`${currentStage}_completed`, { duration_seconds: stageTimers[currentStage] || 0 });
      setCompletedStages(prev => new Set(prev).add(currentStage));
    }

    await logEvent(`${stageKey}_started`);
    setCurrentStage(stageKey);
    setActiveTimer(stageKey);
    toast.success(`Stage: ${MEETING_STAGES.find(s => s.key === stageKey)?.label || stageKey}`);
    loadEvents();
  }, [currentStage, completedStages, stageTimers, user]);

  const completeStage = useCallback(async (stageKey: string) => {
    await logEvent(`${stageKey}_completed`, { duration_seconds: stageTimers[stageKey] || 0 });
    setCompletedStages(prev => new Set(prev).add(stageKey));
    setCurrentStage(null);
    setActiveTimer(null);
    loadEvents();
  }, [stageTimers, user]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const getStageStatus = (key: string) => {
    if (completedStages.has(key)) return "completed";
    if (currentStage === key) return "active";
    return "pending";
  };

  return (
    <Card className="w-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Meeting Flow</CardTitle>
      </CardHeader>
      <CardContent className="p-3">
        <ScrollArea className="max-h-[400px]">
          <div className="space-y-1">
            {MEETING_STAGES.map((stage, idx) => {
              const status = getStageStatus(stage.key);
              return (
                <div key={stage.key} className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-muted/50">
                  {status === "completed" ? (
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                  ) : status === "active" ? (
                    <Play className="h-4 w-4 text-primary shrink-0 animate-pulse" />
                  ) : (
                    <Circle className="h-4 w-4 text-muted-foreground shrink-0" />
                  )}
                  <span className={`text-xs flex-1 ${status === "active" ? "font-semibold text-primary" : status === "completed" ? "text-muted-foreground line-through" : ""}`}>
                    {stage.label}
                  </span>
                  {status === "active" && (
                    <span className="text-xs font-mono text-primary">
                      {formatTime(stageTimers[stage.key] || 0)}
                    </span>
                  )}
                  {isChairOrSecretary && status === "pending" && (
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => startStage(stage.key)}>
                      Start
                    </Button>
                  )}
                  {isChairOrSecretary && status === "active" && (
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => completeStage(stage.key)}>
                      Done
                    </Button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Agenda items during discussion stage */}
          {currentStage === "agenda_discussion" && agendaItems.length > 0 && (
            <>
              <Separator className="my-2" />
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground px-2">Agenda Items</p>
                {agendaItems.map((item, idx) => (
                  <div
                    key={item.id}
                    className={`flex items-center gap-2 py-1 px-2 rounded text-xs ${idx === currentAgendaIndex ? "bg-primary/10 font-medium" : "text-muted-foreground"}`}
                  >
                    <ChevronRight className={`h-3 w-3 ${idx === currentAgendaIndex ? "text-primary" : "opacity-30"}`} />
                    <span className="flex-1">{idx + 1}. {item.title}</span>
                    {isChairOrSecretary && idx === currentAgendaIndex && idx < agendaItems.length - 1 && (
                      <Button size="sm" variant="ghost" className="h-5 px-1 text-[10px]" onClick={() => setCurrentAgendaIndex(idx + 1)}>
                        Next
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
